import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { ClientService } from './client.service';

export interface AnnotationLinkModel {
  id?: number;
  annotationId?: number;
  deckId?: number | null;
  mindMapId?: number | null;
  nodeKey?: string | null;
  deletedAt?: string | null;
}

export type AnnotationLinkUpsertPayload = {
  id?: number;
  annotationId: number;
  deckId?: number | null;
  mindMapId?: number | null;
  nodeKey?: string | null;
  deletedAt?: string | null;
};

export type AnnotationLinkTargetQuery = {
  annotationId?: number;
  deckId?: number;
  mindMapId?: number;
  nodeKey?: string;
};

type AnnotationLinkResponse =
  | AnnotationLinkModel
  | {
      data?: AnnotationLinkModel;
      item?: AnnotationLinkModel;
      value?: AnnotationLinkModel;
    };

type AnnotationLinksResponse =
  | AnnotationLinkModel[]
  | {
      data?: AnnotationLinkModel[];
      items?: AnnotationLinkModel[];
      value?: AnnotationLinkModel[];
    };

@Injectable({ providedIn: 'root' })
export class AnnotationLinkService {
  private readonly client = inject(ClientService);

  get(id: number): Observable<AnnotationLinkModel> {
    return this.client
      .get<AnnotationLinkResponse>(`annotation-link/${id}`)
      .pipe(map((response) => this.unwrapItem(response)));
  }

  list(): Observable<AnnotationLinkModel[]> {
    return this.client
      .get<AnnotationLinksResponse>('annotation-link')
      .pipe(map((response) => this.unwrapList(response)));
  }

  listByAnnotation(annotationId: number): Observable<AnnotationLinkModel[]> {
    return this.client
      .get<AnnotationLinksResponse>(`annotation/${annotationId}/links`)
      .pipe(map((response) => this.unwrapList(response)));
  }

  listByTarget(
    query: AnnotationLinkTargetQuery,
  ): Observable<AnnotationLinkModel[]> {
    const params = new URLSearchParams();
    if (
      typeof query.annotationId === 'number' &&
      Number.isFinite(query.annotationId)
    ) {
      params.set('annotationId', String(query.annotationId));
    }
    if (typeof query.deckId === 'number' && Number.isFinite(query.deckId)) {
      params.set('deckId', String(query.deckId));
    }
    if (
      typeof query.mindMapId === 'number' &&
      Number.isFinite(query.mindMapId)
    ) {
      params.set('mindMapId', String(query.mindMapId));
    }
    if (typeof query.nodeKey === 'string' && query.nodeKey.trim()) {
      params.set('nodeKey', query.nodeKey.trim());
    }

    const queryString = params.toString();
    const route = queryString
      ? `annotation-link/target?${queryString}`
      : 'annotation-link/target';

    return this.client
      .get<AnnotationLinksResponse>(route)
      .pipe(map((response) => this.unwrapList(response)));
  }

  create(
    payload: AnnotationLinkUpsertPayload,
  ): Observable<AnnotationLinkModel> {
    return this.client
      .post<
        AnnotationLinkResponse,
        AnnotationLinkUpsertPayload
      >('annotation-link', payload)
      .pipe(map((response) => this.unwrapItem(response)));
  }

  update(
    payload: AnnotationLinkUpsertPayload,
  ): Observable<AnnotationLinkModel> {
    return this.client
      .put<
        AnnotationLinkResponse,
        AnnotationLinkUpsertPayload
      >('annotation-link', payload)
      .pipe(map((response) => this.unwrapItem(response)));
  }

  delete(id: number): Observable<boolean> {
    return this.client.delete<unknown>(`annotation-link/${id}`).pipe(
      map((response) => {
        if (typeof response === 'boolean') {
          return response;
        }

        if (typeof (response as { data?: unknown })?.data === 'boolean') {
          return Boolean((response as { data?: unknown }).data);
        }

        if (typeof (response as { value?: unknown })?.value === 'boolean') {
          return Boolean((response as { value?: unknown }).value);
        }

        return true;
      }),
    );
  }

  private unwrapItem(response: AnnotationLinkResponse): AnnotationLinkModel {
    const wrapped = response as Record<string, unknown>;
    const data = wrapped['data'] ?? wrapped['Data'];
    const item = wrapped['item'] ?? wrapped['Item'];
    const value = wrapped['value'] ?? wrapped['Value'];

    if (this.isModel(data)) {
      return this.normalize(data);
    }

    if (this.isModel(item)) {
      return this.normalize(item);
    }

    if (this.isModel(value)) {
      return this.normalize(value);
    }

    if (this.isModel(response)) {
      return this.normalize(response);
    }

    return {};
  }

  private unwrapList(response: AnnotationLinksResponse): AnnotationLinkModel[] {
    if (Array.isArray(response)) {
      return response.map((item) => this.normalize(item));
    }

    const wrapped = response as Record<string, unknown>;
    const data = wrapped['data'] ?? wrapped['Data'];
    const items = wrapped['items'] ?? wrapped['Items'];
    const value = wrapped['value'] ?? wrapped['Value'];

    if (Array.isArray(data)) {
      return data.map((item) => this.normalize(item as AnnotationLinkModel));
    }

    if (Array.isArray(items)) {
      return items.map((item) => this.normalize(item as AnnotationLinkModel));
    }

    if (Array.isArray(value)) {
      return value.map((item) => this.normalize(item as AnnotationLinkModel));
    }

    return [];
  }

  private isModel(value: unknown): value is AnnotationLinkModel {
    if (typeof value !== 'object' || value === null) {
      return false;
    }

    const record = value as Record<string, unknown>;
    return (
      'id' in record ||
      'Id' in record ||
      'annotationId' in record ||
      'AnnotationId' in record
    );
  }

  private normalize(model: AnnotationLinkModel): AnnotationLinkModel {
    const record = model as Record<string, unknown>;

    return {
      id: this.toOptionalNumber(record['id'] ?? record['Id']),
      annotationId: this.toOptionalNumber(
        record['annotationId'] ?? record['AnnotationId'],
      ),
      deckId: this.toNullableNumber(record['deckId'] ?? record['DeckId']),
      mindMapId: this.toNullableNumber(
        record['mindMapId'] ?? record['MindMapId'],
      ),
      nodeKey: this.toNullableString(record['nodeKey'] ?? record['NodeKey']),
      deletedAt: this.toNullableString(
        record['deletedAt'] ?? record['DeletedAt'],
      ),
    };
  }

  private toOptionalNumber(value: unknown): number | undefined {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  private toNullableNumber(value: unknown): number | null | undefined {
    if (value == null) {
      return value as null | undefined;
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  private toNullableString(value: unknown): string | null | undefined {
    if (value == null) {
      return value as null | undefined;
    }

    return typeof value === 'string' ? value : String(value);
  }
}
